type TargetUser = { id: number; role?: string | null };

const ALLOWED_GRANT_TARGETS: Record<string, string[]> = {
  owner: ["owner", "hr_manager", "manager", "member", "auditor"],
  hr_manager: ["hr_manager", "manager", "member", "auditor"],
  manager: ["member", "auditor"],
};

const canGrantToTarget = (
  actorRole: string | null | undefined,
  targetRole: string | null | undefined,
) => {
  if (!actorRole || !targetRole) return false;
  const allowed = ALLOWED_GRANT_TARGETS[actorRole];
  return Array.isArray(allowed) && allowed.includes(targetRole);
};

export const useAbac = () => {
  const auth = useAuthStore();

  const can = (resource: string, action: string, context?: any) => {
    // Check if user has the basic permission
    const hasPermission = auth.permissions?.[resource]?.includes(action);
    if (!hasPermission) return false;

    // Handle specific context-based restrictions
    if (resource === "familyGroups" && (action === "grant_access" || action === "revoke_access")) {
      // No user can change their own group access
      if (context?.targetUserId === auth.user?.id) {
        return false;
      }
      // Enforce the role hierarchy for grant/revoke targets.
      // member/auditor actors have no allowed targets, so this returns false.
      const targetRole = context?.targetRole;
      if (targetRole !== undefined) {
        return canGrantToTarget(auth.user?.role, targetRole);
      }
    }

    return true;
  };

  // Helper function specifically for checking if user can edit group access
  const canEditUserGroupAccess = (targetUser: TargetUser) => {
    return (
      can("familyGroups", "grant_access", {
        targetUserId: targetUser.id,
        targetRole: targetUser.role,
      }) &&
      can("familyGroups", "revoke_access", {
        targetUserId: targetUser.id,
        targetRole: targetUser.role,
      })
    );
  };

  const canEditOrganisation = () => can("orga", "edit");

  return { can, canEditUserGroupAccess, canEditOrganisation };
};
